import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/core/hooks/useAuth";
import { abertoNaTelaDeInicio, iosForaDaTelaDeInicio } from "@/core/notificacoes/permissao";
import { pushService } from "@/core/services/pushService";
import { env } from "@/shared/config/env";

type EstadoPush =
  | { tipo: "verificando" }
  | { tipo: "nao_suportado"; motivo: string }
  | { tipo: "pronto"; inscrito: boolean }
  | { tipo: "erro"; mensagem: string };

/**
 * Converte a VAPID public key (base64url) para o formato exigido por
 * `PushManager.subscribe`. Retorna `BufferSource` (em vez de `Uint8Array`)
 * porque o array sempre é alocado sobre um `ArrayBuffer` novo (nunca
 * `SharedArrayBuffer`) — o cast é seguro; sem ele, versões mais novas da lib
 * DOM do TypeScript (que parametrizam TypedArray pelo tipo do buffer)
 * recusam a atribuição a `applicationServerKey` mesmo o valor sendo válido
 * em runtime (achado em `npm run build`, que usa `tsc -b` com libs mais
 * estritas que `npm run typecheck`).
 */
function urlBase64ToUint8Array(base64String: string): BufferSource {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * iOS/Safari só expõe `PushManager` quando o site foi instalado na Tela de
 * Início (modo standalone) e está em 16.4+ — não existe evento equivalente
 * ao `beforeinstallprompt` para detectar isso (mesma limitação documentada
 * em usePwaInstall.ts). Sem suporte nativo, a única forma de orientar o
 * usuário é detectar iOS por user agent e sugerir o passo manual.
 */
function motivoNaoSuportado(): string {
  if (iosForaDaTelaDeInicio(navigator.userAgent, abertoNaTelaDeInicio())) {
    return 'No iPhone ou iPad, notificações exigem que o app esteja instalado na Tela de Início. Toque em "Compartilhar" e depois em "Adicionar à Tela de Início", e abra o app por esse ícone.';
  }
  return "Este navegador não tem suporte a notificações push.";
}

function suportaPush(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    !!env.VITE_VAPID_PUBLIC_KEY
  );
}

/**
 * Orquestra a assinatura de Web Push deste dispositivo usando só APIs
 * nativas do navegador (sem plugin/workbox). Nunca assina sozinho: o hook,
 * ao montar, só LÊ o estado atual (`verificar`) — criar ou reativar uma
 * subscription exige uma ação explícita do usuário (`ativar`), que é o que
 * garante o fluxo de permissão/consentimento exigido pela decisão de
 * lifecycle aprovada 03/09/2026 (reativação nunca é automática no login).
 */
export function usePushSubscription() {
  const { pessoa } = useAuth();
  const [estado, setEstado] = useState<EstadoPush>({ tipo: "verificando" });
  const [processando, setProcessando] = useState(false);

  const verificar = useCallback(async () => {
    if (!suportaPush()) {
      setEstado({ tipo: "nao_suportado", motivo: motivoNaoSuportado() });
      return;
    }
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        setEstado({ tipo: "pronto", inscrito: false });
        return;
      }
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription || Notification.permission !== "granted") {
        setEstado({ tipo: "pronto", inscrito: false });
        return;
      }
      // PN-17 (item B): o navegador ter uma subscription não basta — o
      // dispatcher só envia para linhas `ativo=true`. Consultar o banco
      // aqui é só para exibição (nunca reativa nada sozinho).
      let ativoNoBanco = false;
      try {
        ativoNoBanco = await pushService.estaAtivoNoBanco(subscription.endpoint);
      } catch {
        ativoNoBanco = false;
      }
      setEstado({ tipo: "pronto", inscrito: ativoNoBanco });
    } catch {
      setEstado({ tipo: "erro", mensagem: "Não foi possível verificar o status das notificações." });
    }
  }, []);

  useEffect(() => {
    verificar();
  }, [verificar]);

  const assinarNoNavegador = useCallback(async () => {
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(env.VITE_VAPID_PUBLIC_KEY),
      });
    }
    return subscription;
  }, []);

  const ativar = useCallback(async () => {
    if (!pessoa || !suportaPush()) return;
    setProcessando(true);
    try {
      const permissao = await Notification.requestPermission();
      if (permissao !== "granted") {
        setEstado({ tipo: "pronto", inscrito: false });
        return;
      }

      const subscription = await assinarNoNavegador();
      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error("Assinatura de push inválida.");
      }

      const resultado = await pushService.registrarOuReativar({
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
        userAgent: navigator.userAgent,
        pessoaId: pessoa.id,
      });

      if (resultado === "conflito") {
        // Item 3 da decisão de lifecycle: este endpoint já pertence a outra
        // pessoa (dispositivo compartilhado). Nunca reatribuir — descarta a
        // subscription do navegador e assina de novo, o que gera um
        // endpoint genuinamente diferente, e tenta salvar de novo.
        await subscription.unsubscribe();
        const novaSubscription = await assinarNoNavegador();
        const novoJson = novaSubscription.toJSON();
        if (!novoJson.endpoint || !novoJson.keys?.p256dh || !novoJson.keys?.auth) {
          throw new Error("Assinatura de push inválida.");
        }
        await pushService.registrarOuReativar({
          endpoint: novoJson.endpoint,
          p256dh: novoJson.keys.p256dh,
          auth: novoJson.keys.auth,
          userAgent: navigator.userAgent,
          pessoaId: pessoa.id,
        });
      }

      setEstado({ tipo: "pronto", inscrito: true });
    } catch (erro) {
      setEstado({
        tipo: "erro",
        mensagem: erro instanceof Error ? erro.message : "Não foi possível ativar as notificações.",
      });
    } finally {
      setProcessando(false);
    }
  }, [pessoa, assinarNoNavegador]);

  const desativar = useCallback(async () => {
    setProcessando(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        // PN-17 (item A): não chamar unsubscribe() aqui — desativar é uma
        // decisão do Elos (ativo=false no banco), não uma revogação da
        // subscription do navegador. Preservar a subscription é o que
        // permite que a próxima ativação reative a MESMA linha em vez de
        // criar uma nova (ver pushService.registrarOuReativar).
        await pushService.desativarPorEndpoint(subscription.endpoint);
      }
      setEstado({ tipo: "pronto", inscrito: false });
    } catch (erro) {
      setEstado({
        tipo: "erro",
        mensagem: erro instanceof Error ? erro.message : "Não foi possível desativar as notificações.",
      });
    } finally {
      setProcessando(false);
    }
  }, []);

  return { estado, processando, ativar, desativar };
}
